import type { EducationProgram } from './schema';

/**
 * Education is a *trade*: years and money now, optionality later. Every program
 * has an honest downside — the doctorate costs a decade of compounding, the
 * bootcamp grants no credential shield in a downturn.
 */
const PROGRAMS: EducationProgram[] = [
  {
    id: 'edu.bachelor.general',
    label: 'Bachelor’s degree',
    blurb: 'Four years, real debt, and the key that unlocks most credentialed fields.',
    level: 'bachelor',
    years: 4,
    annualCost: 14500,
    workCapacity: 0.28,
    skillGrowth: { technical: 4, communication: 4, social: 3, selfManagement: 2 },
    credential: 'cred.bachelor',
    minAge: 17,
    maxAge: 48,
    unlocksFields: ['education', 'finance', 'research', 'software', 'creative', 'public'],
    loanFraction: 0.72,
    tags: ['degree', 'long', 'opportunity'],
  },
  {
    id: 'edu.bachelor.stem',
    label: 'Bachelor’s in engineering',
    blurb: 'Harder, narrower, and noticeably better paid on the far side.',
    level: 'bachelor',
    years: 4,
    annualCost: 17200,
    workCapacity: 0.2,
    skillGrowth: { technical: 8, selfManagement: 2.5, communication: 1.5 },
    credential: 'cred.bachelor.stem',
    minAge: 17,
    maxAge: 42,
    requires: [{ k: 'skill', skill: 'technical', op: '>=', value: 18 }],
    unlocksFields: ['software', 'research', 'logistics'],
    loanFraction: 0.75,
    tags: ['degree', 'technical', 'high-return'],
  },
  {
    id: 'edu.vocational',
    label: 'Vocational training',
    blurb: 'Two years, hands-on, and earning while your peers are still in lectures.',
    level: 'vocational',
    years: 2,
    annualCost: 6200,
    workCapacity: 0.62,
    skillGrowth: { technical: 6, selfManagement: 2 },
    credential: 'cred.vocational',
    minAge: 16,
    maxAge: 58,
    unlocksFields: ['trades', 'healthcare', 'logistics'],
    loanFraction: 0.35,
    tags: ['practical', 'fast', 'affordable'],
  },
  {
    id: 'edu.master',
    label: 'Master’s degree',
    blurb: 'Two years to specialise — a real jump for some fields, a detour for others.',
    level: 'master',
    years: 2,
    annualCost: 22000,
    workCapacity: 0.42,
    skillGrowth: { technical: 4.5, communication: 3.5, management: 3, business: 2 },
    credential: 'cred.master',
    minAge: 21,
    maxAge: 55,
    requires: [{ k: 'educationAtLeast', level: 'bachelor' }],
    unlocksFields: ['research', 'law', 'finance'],
    loanFraction: 0.68,
    tags: ['degree', 'specialisation'],
  },
  {
    id: 'edu.mba',
    label: 'MBA',
    blurb: 'Expensive, network-heavy, and the fastest legal route into management.',
    level: 'master',
    years: 2,
    annualCost: 46000,
    workCapacity: 0.35,
    skillGrowth: { business: 8, management: 7, communication: 4, financial: 4 },
    credential: 'cred.mba',
    minAge: 24,
    maxAge: 52,
    requires: [
      { k: 'educationAtLeast', level: 'bachelor' },
      { k: 'num', path: 'yearsExperience', op: '>=', value: 3 },
    ],
    unlocksFields: ['finance', 'logistics', 'sales'],
    loanFraction: 0.7,
    tags: ['degree', 'network', 'expensive'],
  },
  {
    id: 'edu.law',
    label: 'Law school',
    blurb: 'Three years, heavy debt, and a profession that rarely lets go.',
    level: 'master',
    years: 3,
    annualCost: 41000,
    workCapacity: 0.18,
    skillGrowth: { communication: 7, business: 3, social: 2 },
    credential: 'cred.law',
    minAge: 21,
    maxAge: 46,
    requires: [{ k: 'educationAtLeast', level: 'bachelor' }],
    unlocksFields: ['law', 'public'],
    loanFraction: 0.8,
    tags: ['degree', 'credential-gate', 'expensive'],
  },
  {
    id: 'edu.doctorate',
    label: 'Doctorate',
    blurb: 'Five years of poverty-adjacent obsession for a shot at the frontier.',
    level: 'doctorate',
    years: 5,
    annualCost: 3800,
    workCapacity: 0.3,
    skillGrowth: { technical: 6, creative: 3, communication: 2.5, selfManagement: 2 },
    credential: 'cred.doctorate',
    minAge: 22,
    maxAge: 44,
    requires: [{ k: 'educationAtLeast', level: 'master' }],
    unlocksFields: ['research', 'education'],
    loanFraction: 0.1,
    tags: ['degree', 'very-long', 'meaning'],
  },
  {
    id: 'edu.bootcamp',
    label: 'Intensive bootcamp',
    blurb: 'Six months, one skill, no credential. Works — until the market tightens.',
    level: 'vocational',
    years: 1,
    annualCost: 13500,
    workCapacity: 0.45,
    skillGrowth: { technical: 13 },
    credential: 'cred.bootcamp',
    minAge: 18,
    maxAge: 60,
    unlocksFields: ['software', 'creative'],
    loanFraction: 0.5,
    tags: ['fast', 'narrow', 'no-credential-shield'],
  },
  {
    id: 'edu.cert.pm',
    label: 'Management certification',
    blurb: 'One year, part-time, and a legible reason to promote you.',
    level: 'vocational',
    years: 1,
    annualCost: 4200,
    workCapacity: 0.86,
    skillGrowth: { management: 9, communication: 3 },
    credential: 'cred.cert.pm',
    minAge: 21,
    unlocksFields: ['logistics', 'public'],
    tags: ['certification', 'part-time', 'cheap'],
  },
  {
    id: 'edu.cert.finance',
    label: 'Financial analyst certification',
    blurb: 'Brutal exams taken around a full-time job. Respected where it counts.',
    level: 'vocational',
    years: 2,
    annualCost: 3600,
    workCapacity: 0.78,
    skillGrowth: { financial: 10, business: 3 },
    credential: 'cred.cert.finance',
    minAge: 20,
    requires: [{ k: 'skill', skill: 'financial', op: '>=', value: 25 }],
    unlocksFields: ['finance'],
    tags: ['certification', 'part-time', 'hard'],
  },
  {
    id: 'edu.selfStudy',
    label: 'Self-directed study',
    blurb: 'Free, flexible, and entirely dependent on your own discipline.',
    level: 'highschool',
    years: 1,
    annualCost: 400,
    workCapacity: 0.94,
    skillGrowth: { technical: 4, creative: 3, financial: 2, selfManagement: 2 },
    credential: 'cred.selfstudy',
    minAge: 16,
    unlocksFields: [],
    tags: ['free', 'discipline-gated'],
  },
  {
    id: 'edu.cert.care',
    label: 'Care practitioner licence',
    blurb: 'Eighteen months to a licence that is always, everywhere, in demand.',
    level: 'vocational',
    years: 2,
    annualCost: 5400,
    workCapacity: 0.6,
    skillGrowth: { social: 6, technical: 4, communication: 3 },
    credential: 'cred.care',
    minAge: 18,
    maxAge: 60,
    unlocksFields: ['healthcare'],
    loanFraction: 0.4,
    tags: ['licence', 'recession-proof'],
  },
];

export const EDUCATION_PROGRAMS: Record<string, EducationProgram> = Object.fromEntries(
  PROGRAMS.map((p) => [p.id, p]),
);

export const EDUCATION_PROGRAM_LIST: readonly EducationProgram[] = PROGRAMS;

export const CREDENTIAL_LABELS: Record<string, string> = {
  'cred.bachelor': 'Bachelor’s degree',
  'cred.bachelor.stem': 'Engineering degree',
  'cred.vocational': 'Vocational qualification',
  'cred.master': 'Master’s degree',
  'cred.mba': 'MBA',
  'cred.law': 'Law degree',
  'cred.doctorate': 'Doctorate',
  'cred.bootcamp': 'Bootcamp certificate',
  'cred.cert.pm': 'Management certification',
  'cred.cert.finance': 'Financial analyst charter',
  'cred.selfstudy': 'Self-taught portfolio',
  'cred.care': 'Care practitioner licence',
};
